import { useEffect, useMemo, useState } from "react";
import { Check, X, Save, ClipboardCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Iter 171 — Los campos se cargan dinámicamente desde Configuración → Checklist
// (GET /settings/checklist-fields?active=1). Tipos: status (✓/✕, lógica actual),
// dropdown (opciones del administrador; opción `negative` = incidencia) y numeric.
const AUTO_MARK = "🔧 ";  // prefijo de anotación automática

function autoLineFor(field, value) {
  if (field.type === "dropdown") {
    const opt = (field.options || []).find((o) => o.value === value);
    return `${AUTO_MARK}${opt?.auto_note || `${field.label}: ${opt?.label || value}`}`;
  }
  return `${AUTO_MARK}${field.label}`;
}

function isNegative(field, value) {
  if (!field) return false;
  if (field.type === "numeric") return false;
  if (field.type === "dropdown") return !!(field.options || []).find((o) => o.value === value && o.negative);
  return value === "cross";
}

// Sincroniza el bloque de auto-anotaciones en notes con los items negativos.
function syncAutoNotes(prevNotes, items, fieldsById) {
  const lines = (prevNotes || "").split("\n");
  const manualLines = lines.filter((ln) => !ln.startsWith(AUTO_MARK));
  const autoLines = Object.entries(items)
    .filter(([k, v]) => isNegative(fieldsById[k], v))
    .map(([k, v]) => autoLineFor(fieldsById[k], v));
  const merged = [...autoLines, ...manualLines.filter((l) => l !== "")];
  return merged.join("\n");
}

export default function ChecklistModal({ repairId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState([]);
  const fieldsById = useMemo(() => Object.fromEntries(fields.map((f) => [f.id, f])), [fields]);
  const [data, setData] = useState({
    items: {},
    item_notes: {},
    memoria: "",
    cpu_text: "",
    disco: "",
    password: "",
    notes: "",
    created_at: null,
    updated_at: null,
    updated_by: "",
  });
  // Iter 120 — Snapshot inicial para detectar cambios sin guardar.
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Iter 120 — Limpiamos cualquier estado previo antes de cargar el
    // checklist de la reparación actual (evita mezclar datos entre repairs).
    setData({
      items: {}, item_notes: {}, memoria: "", cpu_text: "", disco: "", password: "", notes: "",
      created_at: null, updated_at: null, updated_by: "",
    });
    setInitialSnapshot(null);
    setLoading(true);
    Promise.all([api.get(`/repairs/${repairId}/checklist`), api.get("/settings/checklist-fields")])
      .then(([r, f]) => {
        if (!cancelled) {
          setFields(f.data.fields || []);
          const loaded = { items: r.data.items || {}, item_notes: r.data.item_notes || {}, ...r.data };
          setData((p) => ({ ...p, ...loaded }));
          // Guardamos el snapshot con los mismos 6 campos que enviamos al save.
          setInitialSnapshot(JSON.stringify({
            items: loaded.items || {},
            item_notes: loaded.item_notes || {},
            memoria: loaded.memoria || "",
            cpu_text: loaded.cpu_text || "",
            disco: loaded.disco || "",
            password: loaded.password || "",
            notes: loaded.notes || "",
          }));
        }
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repairId]);

  // Iter 120 — Estado derivado: ¿hay cambios sin guardar?
  const isDirty = useMemo(() => {
    if (initialSnapshot === null) return false;
    const current = JSON.stringify({
      items: data.items || {},
      item_notes: data.item_notes || {},
      memoria: data.memoria || "",
      cpu_text: data.cpu_text || "",
      disco: data.disco || "",
      password: data.password || "",
      notes: data.notes || "",
    });
    return current !== initialSnapshot;
  }, [data, initialSnapshot]);

  const setItem = (key, value) => {
    setData((p) => {
      const items = { ...p.items, [key]: value };
      const notes = syncAutoNotes(p.notes, items, fieldsById);
      return { ...p, items, notes };
    });
  };

  const clearItem = (key) => {
    setData((p) => {
      const items = { ...p.items };
      delete items[key];
      const notes = syncAutoNotes(p.notes, items, fieldsById);
      return { ...p, items, notes };
    });
  };

  // Iter 90 — Handler específico para el campo numérico de ciclos de carga.
  // Solo permite dígitos y hasta 4 cifras. No dispara autoLineFor porque
  // este item no está marcado como cross/yes.
  const setItemNote = (key, txt) => setData((p) => ({ ...p, item_notes: { ...p.item_notes, [key]: txt } }));

  const setNumericItem = (key, raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    const max = fieldsById[key]?.max_digits || 4;
    if (digits.length > max) {
      toast.error(`Solo se permiten números de hasta ${max} cifras.`);
      return;
    }
    setData((p) => {
      const items = { ...p.items };
      if (digits === "") delete items[key];
      else items[key] = digits;
      return { ...p, items };
    });
  };

  const save = async () => {
    if (saving) return; // Iter 120 — Protección extra contra doble click.
    if (!repairId) {
      toast.error("No se puede guardar: falta el ID de la reparación.");
      return;
    }
    // Iter 171 — campos obligatorios (Configuración → Checklist)
    const missing = fields.filter((f) => f.active && f.required && !data.items[f.id] && !(f.type === "dropdown" && f.default_value));
    if (missing.length) {
      toast.error(`Campos obligatorios sin marcar: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        items: data.items,
        item_notes: data.item_notes,
        memoria: data.memoria,
        cpu_text: data.cpu_text,
        disco: data.disco,
        password: data.password,
        notes: data.notes,
      };
      const r = await api.put(`/repairs/${repairId}/checklist`, payload);
      setData((p) => ({ ...p, ...r.data, items: r.data.items || {}, item_notes: r.data.item_notes || {} }));
      // Iter 120 — Actualizar snapshot para que isDirty vuelva a false.
      setInitialSnapshot(JSON.stringify({
        items: r.data.items || {},
        item_notes: r.data.item_notes || {},
        memoria: r.data.memoria || "",
        cpu_text: r.data.cpu_text || "",
        disco: r.data.disco || "",
        password: r.data.password || "",
        notes: r.data.notes || "",
      }));
      toast.success("Checklist guardado correctamente");
      // Iter 126 — Notificar al padre para que refresque el color del botón
      // Checklist con el `alert_level` recién calculado por el backend.
      if (onSaved) onSaved(r.data.alert_level || "ok");
      onClose && onClose();
    } catch (err) {
      // Iter 120 — Registrar contexto del fallo para debug.
      console.error("[checklist] Fallo al guardar", {
        repairId,
        error: err?.response?.data || err?.message,
      });
      toast.error("No se pudo guardar el checklist. Los cambios no se han confirmado.");
    } finally {
      setSaving(false);
    }
  };

  // Iter 120 — Guardia al cerrar con cambios sin guardar.
  const requestClose = () => {
    if (!isDirty) {
      onClose && onClose();
      return;
    }
    const choice = window.confirm(
      "Tienes cambios sin guardar en el checklist.\n\n" +
      "Pulsa 'Aceptar' para GUARDAR Y CERRAR, o 'Cancelar' para volver al checklist.\n" +
      "(Si quieres salir sin guardar, cierra el checklist desde la X tras confirmar aquí.)"
    );
    if (choice) {
      save();
    }
  };

  // Se renderizan solo los campos activos; `fieldsById` incluye también los
  // desactivados para no perder sus auto-anotaciones históricas.
  const col0 = useMemo(() => fields.filter((f) => f.active && (f.col || 0) === 0), [fields]);
  const col1 = useMemo(() => fields.filter((f) => f.active && f.col === 1), [fields]);
  const col2 = useMemo(() => fields.filter((f) => f.active && f.col === 2), [fields]);
  const rowProps = (f) => ({
    key: f.id, keyName: f.id, field: f, value: data.items[f.id], note: data.item_notes?.[f.id],
    onSet: setItem, onClear: clearItem, onSetNumeric: setNumericItem, onSetNote: setItemNote,
  });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={requestClose}>
      <div
        className="bg-white border-2 border-[#111110] shadow-hard w-full max-w-5xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="checklist-modal"
      >
        {/* Cabecera */}
        <div className="px-5 py-3 bg-[#F7F7F4] border-b-2 border-[#111110] flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={18} />
            <h2 className="font-display font-black text-lg">Montaje del Equipo y Pruebas</h2>
            {isDirty && (
              <span className="text-[10px] font-mono text-amber-700 uppercase tracking-widest" data-testid="checklist-dirty">
                · Cambios sin guardar
              </span>
            )}
          </div>
          <button onClick={requestClose} className="p-1.5 hover:bg-white" data-testid="checklist-close">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-[#525252]">Cargando checklist...</div>
        ) : (
          <>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Columna izquierda */}
              <div className="space-y-1.5">
                {col0.map((f) => <CheckRow {...rowProps(f)} />)}
              </div>

              {/* Columna central */}
              <div className="space-y-4">
                {/* Tabla Memoria / CPU / Disco */}
                <div className="border border-[#111110]">
                  <div className="grid grid-cols-3 bg-[#F7F7F4] border-b border-[#111110]">
                    {["Memoria", "CPU", "Disco"].map((h) => (
                      <div key={h} className="px-2 py-1.5 text-xs font-semibold border-r last:border-r-0 border-[#111110]">
                        {h}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 min-h-[70px]">
                    <input
                      value={data.memoria}
                      onChange={(e) => setData((p) => ({ ...p, memoria: e.target.value }))}
                      data-testid="checklist-memoria"
                      placeholder=""
                      className="px-2 py-1.5 text-sm border-r border-[#111110] outline-none focus:bg-[#F0F4E8]"
                    />
                    <input
                      value={data.cpu_text}
                      onChange={(e) => setData((p) => ({ ...p, cpu_text: e.target.value }))}
                      data-testid="checklist-cpu"
                      placeholder=""
                      className="px-2 py-1.5 text-sm border-r border-[#111110] outline-none focus:bg-[#F0F4E8]"
                    />
                    <input
                      value={data.disco}
                      onChange={(e) => setData((p) => ({ ...p, disco: e.target.value }))}
                      data-testid="checklist-disco"
                      placeholder=""
                      className="px-2 py-1.5 text-sm outline-none focus:bg-[#F0F4E8]"
                    />
                  </div>
                </div>

                {/* Items col 1 */}
                <div className="space-y-1.5">
                  {col1.map((f) => <CheckRow {...rowProps(f)} />)}
                </div>

                {/* Sistema operativo (antes Password — campo renombrado por
                    petición del usuario. El field interno se mantiene como
                    `password` para no romper datos ya guardados). */}
                <div className="border border-[#111110]">
                  <div className="bg-[#F7F7F4] border-b border-[#111110] px-2 py-1.5 text-xs font-semibold">
                    Sistema operativo
                  </div>
                  <input
                    value={data.password}
                    onChange={(e) => setData((p) => ({ ...p, password: e.target.value }))}
                    data-testid="checklist-os"
                    type="text"
                    className="w-full px-2 py-2 text-sm font-mono outline-none focus:bg-[#F0F4E8]"
                  />
                </div>
              </div>

              {/* Columna derecha */}
              <div className="space-y-1.5">
                {col2.map((f) => <CheckRow {...rowProps(f)} />)}
              </div>
            </div>

            {/* Historial / Anotaciones */}
            <div className="px-5 pb-5">
              <div className="border-2 border-[#111110]">
                <div className="bg-[#F7F7F4] border-b-2 border-[#111110] px-3 py-2 flex items-center justify-between">
                  <span className="font-display font-black text-sm">Historial / Anotaciones *</span>
                  <span className="text-[10px] font-mono text-[#525252]">
                    * Todas las anotaciones deberán ir siempre en sistema. {AUTO_MARK} = añadido automático por Cruz
                  </span>
                </div>
                <textarea
                  value={data.notes}
                  onChange={(e) => setData((p) => ({ ...p, notes: e.target.value }))}
                  rows={8}
                  data-testid="checklist-notes"
                  placeholder="Notas, observaciones, mediciones, contraseñas patrón, etc."
                  className="w-full p-3 text-sm font-mono outline-none focus:bg-[#F0F4E8] resize-vertical min-h-[150px]"
                />
              </div>
              {(data.created_at || data.updated_at) && (
                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#525252] flex flex-wrap gap-3">
                  {data.created_at && <span>Creado: {new Date(data.created_at).toLocaleString("es-ES")}</span>}
                  {data.updated_at && <span>Modificado: {new Date(data.updated_at).toLocaleString("es-ES")}</span>}
                  {data.updated_by && <span>Por: {data.updated_by}</span>}
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="px-5 pb-5 pt-2 border-t border-[#E5E5E0] flex items-center gap-3 justify-end sticky bottom-0 bg-white">
              <button
                onClick={requestClose}
                data-testid="checklist-cancel"
                className="px-4 py-2 text-sm border border-[#111110] hover:bg-[#F7F7F4]"
              >
                Cancelar
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="checklist-save"
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-[#6E8B3D] hover:bg-[#556C2E] text-white disabled:opacity-50"
              >
                <Save size={14} /> {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CheckRow({ keyName, field, value, note, onSet, onClear, onSetNumeric, onSetNote }) {
  const label = field.label + (field.required ? " *" : "");
  const isNumeric = field.type === "numeric";
  const isDropdown = field.type === "dropdown";
  const maxDigits = field.max_digits || 4;
  const isYesNo = false;
  const noteBox = field.allow_notes ? (
    <input
      type="text"
      value={note || ""}
      onChange={(e) => onSetNote(keyName, e.target.value)}
      placeholder="Nota…"
      data-testid={`checklist-${keyName}-note`}
      className="ml-auto w-28 h-7 px-2 border border-[#D5D7CE] text-xs outline-none focus:border-[#6E8B3D]"
    />
  ) : null;
  // Modo numérico: input de dígitos.
  if (isNumeric) {
    return (
      <div className="flex items-center gap-2" data-testid={`checklist-row-${keyName}`}>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={value || ""}
          onChange={(e) => onSetNumeric(keyName, e.target.value)}
          maxLength={maxDigits || 4}
          data-testid={`checklist-${keyName}-input`}
          placeholder="0"
          className="w-16 h-7 px-2 border-2 border-[#111110] text-sm font-mono outline-none focus:bg-[#F0F4E8] text-right"
        />
        <span className="text-sm text-[#111110] truncate">{label}</span>
        {noteBox}
      </div>
    );
  }
  // Modo desplegable: opciones definidas en Configuración → Checklist.
  if (isDropdown) {
    const opts = (field.options || []).filter((o) => o.active !== false || o.value === value);
    const current = value || field.default_value || "";
    const negative = !!opts.find((o) => o.value === current && o.negative);
    return (
      <div className="flex items-center gap-2" data-testid={`checklist-row-${keyName}`}>
        <select
          value={current}
          onChange={(e) => (e.target.value ? onSet(keyName, e.target.value) : onClear(keyName))}
          data-testid={`checklist-${keyName}-select`}
          className={`h-7 max-w-[210px] px-1.5 border-2 border-[#111110] text-xs font-medium outline-none focus:bg-[#F0F4E8] ${
            negative ? "bg-amber-100" : "bg-white"
          }`}
        >
          {!field.default_value && <option value="">—</option>}
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="text-sm text-[#111110] leading-tight">{label}</span>
        {noteBox}
      </div>
    );
  }
  // Modo por defecto: dos botones (check / cross), como venía haciendo.
  const isCheck = value === "check";
  const isCross = value === "cross";
  return (
    <div className="flex items-center gap-2" data-testid={`checklist-row-${keyName}`}>
      <button
        type="button"
        onClick={() => (isCheck ? onClear(keyName) : onSet(keyName, "check"))}
        data-testid={`checklist-${keyName}-check`}
        title={isYesNo ? "SÍ" : "Correcto"}
        className={`w-7 h-7 border-2 border-[#111110] inline-flex items-center justify-center transition-colors ${
          isCheck ? "bg-[#6E8B3D] text-white" : "bg-white hover:bg-[#F0F4E8]"
        }`}
      >
        {isCheck ? (isYesNo ? <span className="text-[10px] font-black">SÍ</span> : <Check size={14} strokeWidth={3} />) : null}
      </button>
      <button
        type="button"
        onClick={() => (isCross ? onClear(keyName) : onSet(keyName, "cross"))}
        data-testid={`checklist-${keyName}-cross`}
        title={isYesNo ? "NO" : "Incorrecto"}
        className={`w-7 h-7 border-2 border-[#111110] inline-flex items-center justify-center transition-colors ${
          isCross ? "bg-red-600 text-white" : "bg-white hover:bg-red-50"
        }`}
      >
        {isCross ? (isYesNo ? <span className="text-[10px] font-black">NO</span> : <X size={14} strokeWidth={3} />) : null}
      </button>
      <span className={`text-sm ${value ? "font-medium" : "text-[#111110]"} truncate`}>
        {label}
      </span>
      {noteBox}
    </div>
  );
}
